import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';

import { RootState } from '../..';
import { createTutorRating, getTutorRatingByCourse } from './api';
import {
  adminGetTutorRatings,
  adminUpdateTutorRating,
  adminDeleteTutorRating,
} from './adminApi';

export interface ITutorRating {
  _id: string;
  courseId: string;
  rating: number;
  feedback: string;
  student?: {
    _id: string;
    firstname: string;
    lastname: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ITutorRatingsState {
  status: 'idle' | 'loading' | 'rejected';
  existingRating: ITutorRating | null;
  adminRatingsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: ITutorRating[];
  };
}

const initialState: ITutorRatingsState = {
  status: 'idle',
  existingRating: null,
  adminRatingsData: undefined,
};

export const TutorRatingsSlice = createSlice({
  name: 'tutorRatings',
  initialState,
  reducers: {
    resetTutorRating: (state) => {
      state.existingRating = null;
    },
    resetAdminRatingsData: (state) => {
      state.adminRatingsData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTutorRatingByCourse.fulfilled, (state, action) => {
        state.existingRating = action.payload;
        state.status = 'idle';
      })
      .addCase(createTutorRating.fulfilled, (state, action) => {
        state.existingRating = action.payload;
        state.status = 'idle';
      })
      .addCase(adminGetTutorRatings.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminRatingsData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };
        state.status = 'idle';
      })
      .addCase(adminUpdateTutorRating.fulfilled, (state, action) => {
        const updated = action.payload;

        if (state.adminRatingsData) {
          const index = state.adminRatingsData.list.findIndex(
            (item) => item._id === updated._id
          );

          if (index > -1) {
            state.adminRatingsData.list[index] = updated;
          }
        }

        state.status = 'idle';
      })
      .addCase(adminDeleteTutorRating.fulfilled, (state, action) => {
        const deletedId = action.payload;

        if (state.adminRatingsData) {
          state.adminRatingsData.list = state.adminRatingsData.list.filter(
            (item) => item._id !== deletedId
          );

          if (state.adminRatingsData.totalDocs) {
            state.adminRatingsData.totalDocs -= 1;
          }
        }

        state.status = 'idle';
      });

    builder.addMatcher(
      isPending(
        getTutorRatingByCourse,
        createTutorRating,
        adminGetTutorRatings,
        adminUpdateTutorRating,
        adminDeleteTutorRating
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        getTutorRatingByCourse,
        createTutorRating,
        adminGetTutorRatings,
        adminUpdateTutorRating,
        adminDeleteTutorRating
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getTutorRatingsState = (
  state: RootState
): RootState['tutorRatings'] => state.tutorRatings;

export const {
  resetTutorRating,
  resetAdminRatingsData,
} = TutorRatingsSlice.actions;

export * from './api';
export * from './adminApi';
export default TutorRatingsSlice.reducer;
